import { defineStore } from "pinia";
import axios from "axios";
import dayjs from "dayjs";
export const useFetchDataStore = defineStore("fetchData", {
  state: () => ({
    user_accounts: [],
    schedules: [],
    attendances: [],
    attendancePagination: {
      total: 0,
      page: 1,
      limit: 10,
      totalPages: 1,
    },
    employee_details: [],
    client_details: [],
    onboard_details: [],
    /** Per-resource loading for skeletons (supports concurrent fetches). */
    loadingByResource: {
      employees: false,
      employee_detail: false,
      user_accounts: false,
      schedules: false,
      attendances: false,
      clients: false,
      onboard: false,
    },
    error: null,
    notificationsCount: 0,
    currentUser: null,
  }),
  actions: {
    // ---------------------
    // FETCHING DATA
    // ---------------------
    async fetchEmployeeDetails() {
      this.loadingByResource.employees = true;
      this.error = null;
      try {
        const response = await axios.get(
          `${process.env.VUE_APP_API_BASE_URL}/employee-details/get-employee-details`,
        );
        this.employee_details = response.data;
      } catch (err) {
        this.error = err.message || "Failed to fetch employee details";
      } finally {
        this.loadingByResource.employees = false;
      }
    },
    // ✅ Fetch ONE employee by employee_id
    async fetchEmployeeById(id) {
      this.loadingByResource.employee_detail = true;
      this.error = null;

      try {
        const response = await axios.get(
          `${process.env.VUE_APP_API_BASE_URL}/employee-details/get-employee-details`,
          {
            params: { employee_id: id },
          },
        );

        // API returns an array → take first result
        this.selected_employee = response.data[0] || null;
      } catch (err) {
        this.error = err.message || "Failed to fetch employee";
      } finally {
        this.loadingByResource.employee_detail = false;
      }
    },

    async fetchUserAccounts() {
      this.loadingByResource.user_accounts = true;
      this.error = null;
      try {
        const response = await axios.get(
          `${process.env.VUE_APP_API_BASE_URL}/user/get-user`,
          { withCredentials: true },
        );
        this.user_accounts = response.data;
        this.updateNotificationsCount();
      } catch (err) {
        this.error = err.message || "Failed to fetch user_accounts";
      } finally {
        this.loadingByResource.user_accounts = false;
      }
    },

    /** Merge a row returned from PATCH /user/update (or create) into the cached list. */
    mergeUserAccountRow(updated) {
      if (!updated || updated.user_id == null) return;
      const id = Number(updated.user_id);
      const list = this.user_accounts;
      const i = list.findIndex((u) => Number(u.user_id) === id);
      if (i !== -1) {
        this.user_accounts[i] = { ...list[i], ...updated };
        this.updateNotificationsCount();
      }
    },

    async fetchSchedules() {
      this.loadingByResource.schedules = true;
      this.error = null;
      try {
        const response = await axios.get(
          `${process.env.VUE_APP_API_BASE_URL}/schedule/get-schedule`,
          { withCredentials: true },
        );
        this.schedules = response.data;
        this.updateNotificationsCount();
      } catch (err) {
        this.error = err.message || "Failed to fetch schedules";
      } finally {
        this.loadingByResource.schedules = false;
      }
    },

    async fetchAttendances(opts = {}) {
      const page = opts.page ?? this.attendancePagination.page;
      const limit = opts.limit ?? this.attendancePagination.limit;
      this.loadingByResource.attendances = true;
      this.error = null;
      try {
        const response = await axios.get(
          `${process.env.VUE_APP_API_BASE_URL}/attendance/get-attendance`,
          { params: { page, limit }, withCredentials: true },
        );
        const body = response.data;
        this.attendances = body.data ?? [];
        this.attendancePagination = {
          total: body.total ?? 0,
          page: body.page ?? page,
          limit: body.limit ?? limit,
          totalPages: body.totalPages ?? 1,
        };
      } catch (err) {
        this.error = err.message || "Failed to fetch attendances";
      } finally {
        this.loadingByResource.attendances = false;
      }
    },
    async fetchClientDetails() {
      this.loadingByResource.clients = true;
      this.error = null;
      const base = process.env.VUE_APP_API_BASE_URL;
      try {
        const me = await axios.get(`${base}/auth/me`, {
          withCredentials: true,
        });
        const code = String(me.data?.role_code || "").toUpperCase();
        const url =
          code === "VAM"
            ? `${base}/client-details/scope/me`
            : `${base}/client-details/get-all-client-details`;
        const response = await axios.get(url, { withCredentials: true });
        this.client_details = response.data;
        this.updateNotificationsCount();
      } catch (err) {
        this.error = err.message || "Failed to fetch client_details";
      } finally {
        this.loadingByResource.clients = false;
      }
    },
    async fetchOnboardDetails() {
      this.loadingByResource.onboard = true;
      this.error = null;
      try {
        const response = await axios.get(
          `${process.env.VUE_APP_API_BASE_URL}/onboard-details/get-all-onboard-details`,
          { withCredentials: true },
        );
        this.onboard_details = response.data;
        this.updateNotificationsCount();
      } catch (err) {
        this.error = err.message || "Failed to fetch onboard_details";
      } finally {
        this.loadingByResource.onboard = false;
      }
    },

    /** VAM or Provider: server-scoped onboard list. */
    async fetchOnboardDetailsScoped() {
      this.loadingByResource.onboard = true;
      this.error = null;
      try {
        const response = await axios.get(
          `${process.env.VUE_APP_API_BASE_URL}/onboard-details/scope/me`,
          { withCredentials: true },
        );
        this.onboard_details = response.data;
        this.updateNotificationsCount();
      } catch (err) {
        this.error = err.message || "Failed to fetch onboard assignments";
      } finally {
        this.loadingByResource.onboard = false;
      }
    },

    // ---------------------
    // ADD / UPDATE DATA
    // ---------------------
    async addUser(user) {
      const res = await axios.post(
        `${process.env.VUE_APP_API_BASE_URL}/user/add`,
        user,
      );
      this.user_accounts.push(res.data);
      this.updateNotificationsCount();
    },

    async updateUser(user) {
      await axios.put(
        `${process.env.VUE_APP_API_BASE_URL}/user/update/${user.user_id}`,
        user,
      );
      const index = this.user_accounts.findIndex(
        (u) => u.user_id === user.user_id,
      );
      if (index !== -1) this.user_accounts[index] = { ...user };
      this.updateNotificationsCount();
    },

    // ---------------------
    // NOTIFICATIONS COUNT
    // ---------------------
    updateNotificationsCount(currentUser = null) {
      if (!currentUser) currentUser = this.currentUser;

      const today = dayjs().format("YYYY-MM-DD");
      const now = dayjs();
      const rc = String(currentUser?.role_code || "").toUpperCase();
      const isHrStaff = rc === "ADMIN" || rc === "HR";

      // -----------------------------
      // 1. USER / ACCOUNT NOTIFICATIONS (matches NotificationPage.loadUsers)
      // HR & Admin only; pending-like + approved rows until cleared
      // -----------------------------
      let userNotifications = 0;
      if (currentUser && isHrStaff) {
        userNotifications = this.user_accounts.filter((u) => {
          if (u.clear_status === "Cleared") return false;
          const a = u.approval;
          const pendingLike =
            a === null || a === undefined || a === "Pending" || a === "";
          const approved = a === "Approved";
          return pendingLike || approved;
        }).length;
      }

      // -----------------------------
      // 2. CLIENT APPROVALS (VAM → admin review; ADMIN/HR only)
      // -----------------------------
      let clientApprovalNotifications = 0;
      if (currentUser && isHrStaff) {
        const clients = this.client_details || [];
        clientApprovalNotifications = clients.filter((c) => {
          const s = String(c?.admin_approval_status ?? "Approved").trim();
          return s === "Pending";
        }).length;
      }

      // -----------------------------
      // 3. SCHEDULE NOTIFICATIONS
      // -----------------------------
      let scheduleNotifications = 0;

      if (currentUser) {
        const isUpcoming = (s) => {
          const endTime = dayjs(`${s.date} ${s.end}`, "YYYY-MM-DD HH:mm");
          return endTime.isAfter(now);
        };

        if (rc === "ADMIN" || rc === "HR") {
          scheduleNotifications = this.schedules.filter(
            (s) => s.date === today && isUpcoming(s),
          ).length;
        } else if (currentUser.employeeID) {
          scheduleNotifications = this.schedules.filter(
            (s) =>
              s.date === today &&
              (s.tagEmployees || []).some(
                (t) => t.employeeID === currentUser.employeeID,
              ) &&
              isUpcoming(s),
          ).length;
        }
      }

      // -----------------------------
      // TOTAL BADGE COUNT
      // -----------------------------
      this.notificationsCount =
        userNotifications +
        clientApprovalNotifications +
        scheduleNotifications;
    },
  },
});
